import { Building2, ExternalLink } from 'lucide-react';
import { useDialogStore } from '@/store';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

type Props = {
  clientName: string;
  clientID: number;
  storeID: number;
};

function ClientColumn({ clientID, clientName, storeID }: Props) {
  const { openClientCardDialog } = useDialogStore();

  const handleClick = () => {
    openClientCardDialog(clientID);
  };

  const getStoreConfig = (storeID: number) => {
    switch (storeID) {
      case 1:
        return {
          name: 'مخزن القطاعي',
          color: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800/50',
          icon: '🏪'
        };
      case 2:
        return {
          name: 'مخزن الجملة',
          color: 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-950/30 dark:text-purple-400 dark:border-purple-800/50',
          icon: '🏬'
        };
      default:
        return {
          name: 'مخازن أخرى',
          color: 'bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-950/30 dark:text-slate-400 dark:border-slate-800/50',
          icon: '📦'
        };
    }
  };

  const storeConfig = getStoreConfig(storeID);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open details for ${clientName}`}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
      className={cn(
        "group flex h-16 w-full cursor-pointer items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 transition-all duration-200",
        "hover:border-primary hover:bg-primary/5 hover:shadow-md",
        "focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary",
        "dark:border-slate-700 dark:bg-slate-800 dark:hover:border-primary dark:hover:bg-primary/10"
      )}
    >
      {/* Client Icon */}
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 transition-colors group-hover:bg-primary/20">
        <Building2 className="h-5 w-5 text-primary" />
      </div>
      
      {/* Client Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="line-clamp-1 font-medium text-slate-900 dark:text-slate-100 text-sm group-hover:text-primary transition-colors">
            {clientName}
          </h3>
          <ExternalLink className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <div className="flex items-center gap-2">
          <Badge 
            variant="outline" 
            className={cn("gap-1.5 px-2 h-5 text-xs font-medium", storeConfig.color)}
          >
            <span>{storeConfig.icon}</span>
            <span>{storeConfig.name}</span>
          </Badge>
          <Badge variant="outline" className="h-5 px-2 font-mono text-xs text-slate-600 dark:text-slate-400">
            #{clientID}
          </Badge>
        </div>
      </div>
      
      {/* Hover indicator */}
      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
      </div>
    </div>
  );
}

export default ClientColumn;
